import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["viewport", "content", "spacer"]
  static values = {
    itemHeight: { type: Number, default: 80 },
    bufferSize: { type: Number, default: 5 },
    totalItems: Number
  }

  connect() {
    this.visibleItems = []
    this.scrollTop = 0
    this.viewportHeight = 0
    this.startIndex = 0
    this.endIndex = 0
    
    this.setupViewport()
    this.handleScroll()
    this.observeResize()
  }

  disconnect() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }

  setupViewport() {
    if (this.hasViewportTarget) {
      this.viewportHeight = this.viewportTarget.clientHeight
      this.viewportTarget.addEventListener('scroll', this.throttledScroll.bind(this))
    }
  }

  observeResize() {
    this.resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        if (entry.target === this.viewportTarget) {
          this.viewportHeight = entry.contentRect.height
          this.handleScroll()
        }
      }
    })
    
    if (this.hasViewportTarget) {
      this.resizeObserver.observe(this.viewportTarget)
    }
  }

  throttledScroll() {
    if (!this.scrollFrame) {
      this.scrollFrame = requestAnimationFrame(() => {
        this.handleScroll()
        this.scrollFrame = null
      })
    }
  }

  handleScroll() {
    if (!this.hasViewportTarget) return
    
    this.scrollTop = this.viewportTarget.scrollTop
    
    // Calculate visible range
    const visibleStartIndex = Math.floor(this.scrollTop / this.itemHeightValue)
    const visibleEndIndex = Math.ceil((this.scrollTop + this.viewportHeight) / this.itemHeightValue)
    
    // Add buffer for smooth scrolling
    this.startIndex = Math.max(0, visibleStartIndex - this.bufferSizeValue)
    this.endIndex = Math.min(this.totalItemsValue - 1, visibleEndIndex + this.bufferSizeValue)
    
    this.renderVisibleItems()
  }

  renderVisibleItems() {
    if (!this.hasContentTarget) return
    
    // Calculate spacer heights
    const topSpacerHeight = this.startIndex * this.itemHeightValue
    const bottomSpacerHeight = (this.totalItemsValue - this.endIndex - 1) * this.itemHeightValue
    
    // Update spacers
    if (this.hasSpacerTarget) {
      const spacers = this.spacerTargets
      if (spacers[0]) spacers[0].style.height = `${topSpacerHeight}px`
      if (spacers[1]) spacers[1].style.height = `${bottomSpacerHeight}px`
    }
    
    // Get visible items data
    const visibleItemsData = this.getVisibleItemsData(this.startIndex, this.endIndex)
    
    // Update content
    this.updateContent(visibleItemsData)
  }

  getVisibleItemsData(startIndex, endIndex) {
    // This method should be overridden or configured to fetch actual data
    // For now, dispatch a custom event to request data
    const event = new CustomEvent('virtual-scroll:request-items', {
      detail: {
        startIndex,
        endIndex,
        callback: (items) => this.updateContent(items)
      }
    })
    
    this.element.dispatchEvent(event)
    
    return []
  }

  updateContent(items) {
    if (!this.hasContentTarget) return
    
    // Generate HTML for visible items
    const html = items.map((item, index) => this.renderItem(item, this.startIndex + index)).join('')
    
    this.contentTarget.innerHTML = html
  }

  renderItem(item, index) {
    // Default render function - should be customized
    return `
      <div class="virtual-item" data-index="${index}" style="height: ${this.itemHeightValue}px;">
        ${item.content || `Item ${index}`}
      </div>
    `
  }

  scrollToIndex(index) {
    if (!this.hasViewportTarget) return
    
    const targetScrollTop = index * this.itemHeightValue
    this.viewportTarget.scrollTop = targetScrollTop
  }

  scrollToBottom() {
    if (!this.hasViewportTarget) return
    
    const maxScrollTop = (this.totalItemsValue * this.itemHeightValue) - this.viewportHeight
    this.viewportTarget.scrollTop = maxScrollTop
  }

  updateTotalItems(value) {
    this.totalItemsValue = value
    this.handleScroll()
  }

  // Public API for updating items
  addItem() {
    this.totalItemsValue += 1
    this.handleScroll()
  }

  removeItem(index) {
    this.totalItemsValue -= 1
    if (index <= this.startIndex) {
      this.startIndex = Math.max(0, this.startIndex - 1)
    }
    this.handleScroll()
  }

  refresh() {
    this.handleScroll()
  }
}